import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Player, type PlayerRef } from '@remotion/player';
import type { Caption } from '@remotion/captions';
import type { WhisperWebLanguage, WhisperWebModel } from '@remotion/whisper-web';

import { SubtitledVideo } from './composition/SubtitledVideo';
import { CAPTION_STYLES, type CaptionStyleId } from './composition/captionStyles';
import { WEBGPU_MODELS, type WebGpuModelId } from './lib/asr/webgpu';
import type { ModelDownload } from './lib/asr/downloadProgress';
import {
  checkSupport,
  describeEnvironment,
  detectEngine,
  ENGINES,
  formatBytes,
  probeWebGPU,
  type EngineId,
  listModels,
  probeVideo,
  STAGE_LABEL,
  transcribeFile,
  type ModelInfo,
  type Stage,
  type VideoMeta,
} from './lib/pipeline';
import { exportSubtitledVideo } from './lib/exportVideo';
import { captionsToSrt } from './lib/srt';
import { createHeapTracker, formatPeak } from './lib/memory';
import { nudgeCaption, removeCaption, shiftCaptions } from './lib/captions';
import { seekFrameFor } from './lib/timeline';
import {
  CAPTION_FONTS,
  loadCaptionFont,
  loadCustomFont,
  SYSTEM_STACK,
  type CaptionFont,
} from './lib/fonts';
import {
  deleteTranscript,
  describeAge,
  fileKey,
  loadTranscript,
  saveTranscript,
} from './lib/store';
import { Filmstrip } from './components/Filmstrip';
import { Transcript } from './components/Transcript';
import { ErrorBoundary } from './components/ErrorBoundary';

const ACCENTS = ['#F5E14B', '#4BE3A2', '#59B4FF', '#FF6BAA', '#FFFFFF'];

const LANGUAGES: { id: WhisperWebLanguage; label: string }[] = [
  { id: 'auto', label: 'Detect automatically' },
  { id: 'en', label: 'English' },
  { id: 'es', label: 'Spanish' },
  { id: 'nl', label: 'Dutch' },
  { id: 'de', label: 'German' },
  { id: 'fr', label: 'French' },
  { id: 'pt', label: 'Portuguese' },
  { id: 'it', label: 'Italian' },
];

const download = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Give the browser a beat to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

export const App: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [meta, setMeta] = useState<VideoMeta | null>(null);

  const [engineId, setEngineId] = useState<EngineId>('gpu');
  const [model, setModel] = useState<WhisperWebModel>('tiny.en');
  const [gpuModel, setGpuModel] = useState<WebGpuModelId>(
    'onnx-community/whisper-base_timestamped',
  );
  const [language, setLanguage] = useState<WhisperWebLanguage>('auto');
  const [models, setModels] = useState<ModelInfo[]>([]);

  const [stage, setStage] = useState<Stage>('idle');
  const [progress, setProgress] = useState(0);
  const [modelDownload, setModelDownload] = useState<ModelDownload | null>(null);
  const [captions, setCaptions] = useState<Caption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [failedStage, setFailedStage] = useState<Stage | null>(null);
  // setStage is async, so the catch block cannot read the failing stage from
  // state — mirror it into a ref.
  const stageRef = useRef<Stage>('idle');

  const [styleId, setStyleId] = useState<CaptionStyleId>('tiktok');
  const [accent, setAccent] = useState(ACCENTS[0]);
  const [fontId, setFontId] = useState('system');
  const [fontFamily, setFontFamily] = useState(SYSTEM_STACK);
  const [fontBusy, setFontBusy] = useState(false);

  const [rangeStart, setRangeStart] = useState(0);
  const [rangeEnd, setRangeEnd] = useState(0);
  const [exportRangeOnly, setExportRangeOnly] = useState(false);
  const [playheadSec, setPlayheadSec] = useState(0);
  const [restored, setRestored] = useState<string | null>(null);

  const [exporting, setExporting] = useState(false);
  const [exportPct, setExportPct] = useState(0);
  const [exportNote, setExportNote] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const playerRef = useRef<PlayerRef>(null);

  const isolated = typeof window !== 'undefined' && window.crossOriginIsolated;
  const engine = useMemo(() => detectEngine(), []);

  // A range is "active" whenever the handles aren't at the extremes — direct
  // manipulation, rather than a checkbox that can disagree with the handles.
  const hasRange = Boolean(
    meta && (rangeStart > 0.02 || rangeEnd < meta.durationInSeconds - 0.02),
  );
  const [gpu, setGpu] = useState<'yes' | 'no-adapter' | 'unsupported' | null>(null);

  const busy = stage === 'extracting-audio' || stage === 'downloading-model' || stage === 'transcribing';

  // The model is a dozen files fetched in sequence, and until one of them
  // reports a size there is no honest fraction to draw — animate rather than
  // print a number that would only jump once the real sizes arrive.
  const waiting = stage === 'downloading-model' && !modelDownload?.determinate;
  const progressDetail = waiting
    ? 'connecting…'
    : stage === 'downloading-model' && modelDownload
      ? `${Math.round(modelDownload.progress * 100)}% · ${formatBytes(modelDownload.loadedBytes)} of ${formatBytes(modelDownload.totalBytes)}`
      : `${Math.round(progress * 100)}%`;

  useEffect(() => {
    setModels(listModels());
    probeWebGPU().then(setGpu);
  }, []);

  // Mirror the Player's playhead onto the filmstrip.
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !meta) return;
    const onFrame = (e: { detail: { frame: number } }) => {
      setPlayheadSec(e.detail.frame / meta.fps);
    };
    player.addEventListener('frameupdate', onFrame);
    return () => player.removeEventListener('frameupdate', onFrame);
  }, [meta]);

  // Transcribing a long clip is minutes of work held only in memory, and an
  // export in flight cannot be resumed. Warn before it is thrown away.
  useEffect(() => {
    if (!busy && !exporting) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy, exporting]);

  // Persist settled transcripts only — never the streaming partials, which
  // would write on every chunk and can be superseded a moment later.
  useEffect(() => {
    if (!file || busy || captions.length === 0) return;
    const timer = setTimeout(() => {
      void saveTranscript({
        key: fileKey(file),
        captions,
        savedAt: Date.now(),
        fileName: file.name,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [file, busy, captions]);

  // Object URLs leak if we don't release the previous one.
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onPick = useCallback(async (picked: File) => {
    setError(null);
    setCaptions([]);
    setStage('probing');
    setFile(picked);
    try {
      const probed = await probeVideo(picked);
      setMeta(probed);
      setRangeStart(0);
      setRangeEnd(probed.durationInSeconds);
      setPlayheadSec(0);
      setStage('idle');

      // Same file as last time? Bring its transcript back rather than making
      // the user pay for the same inference twice.
      const saved = await loadTranscript(fileKey(picked));
      if (saved && saved.captions.length > 0) {
        setCaptions(saved.captions);
        setRestored(describeAge(saved.savedAt, Date.now()));
        setStage('ready');
      }
    } catch (e) {
      setMeta(null);
      setFailedStage('probing');
      setStage('error');
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const run = useCallback(async () => {
    if (!file) return;
    setError(null);
    setFailedStage(null);
    setRestored(null);
    setOffsetMs(0);
    setCaptions([]);
    setModelDownload(null);
    try {
      // Only the WASM engine needs SharedArrayBuffer / IndexedDB quota; the
      // WebGPU engine has entirely different requirements.
      if (engineId === 'cpu') {
        const support = await checkSupport(model);
        if (!support.supported) {
          throw new Error(
            `Cannot run Whisper here: ${support.detailedReason ?? support.reason}`,
          );
        }
      } else if (gpu !== 'yes') {
        throw new Error(
          'No WebGPU adapter available in this browser. Switch the engine to WASM, or use Chrome 113+.',
        );
      }

      const result = await transcribeFile(file, {
        engine: engineId,
        cpuModel: model,
        gpuModel,
        language,
        range: hasRange ? { startSec: rangeStart, endSec: rangeEnd } : null,
      }, {
        onStage: (s: Stage) => {
          stageRef.current = s;
          setStage(s);
        },
        onProgress: setProgress,
        onDownload: setModelDownload,
        onPartial: setCaptions,
      });
      setCaptions(result);
      setStage('ready');
    } catch (e) {
      // Report the stage that actually failed. Setting stage to 'error' first
      // would discard exactly the information needed to diagnose this.
      setFailedStage(stageRef.current);
      setStage('error');
      setError(e instanceof Error ? e.message : String(e));
      // eslint-disable-next-line no-console
      console.error(`[undertone] failed during ${stageRef.current}`, e);
    }
  }, [file, model, gpuModel, language, engineId, gpu, hasRange, rangeStart, rangeEnd]);

  const doExport = useCallback(async () => {
    if (!src || !meta) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setExporting(true);
    setExportPct(0);
    setError(null);
    try {
      const heap = createHeapTracker();
      const { blob, extension, choice } = await exportSubtitledVideo({
        src,
        meta,
        captions,
        styleId,
        accent,
        fontFamily,
        frameRange:
          hasRange && exportRangeOnly
            ? [
                Math.floor(rangeStart * meta.fps),
                Math.max(Math.ceil(rangeEnd * meta.fps) - 1, Math.floor(rangeStart * meta.fps)),
              ]
            : null,
        signal: controller.signal,
        onProgress: ({ progress: p }) => {
          heap.sample();
          setExportPct(p);
        },
      });
      const base = (file?.name ?? 'video').replace(/\.[^.]+$/, '');
      // Extension must follow the negotiated container, not a hardcoded .mp4 —
      // a webm written as .mp4 refuses to open in most players.
      download(blob, `${base}-subtitled.${extension}`);
      const mb = (blob.size / 1048576).toFixed(1);
      setExportNote(
        `${choice.container} · ${choice.videoCodec}${
          choice.audioCodec ? ` + ${choice.audioCodec}` : ' · no audio encoder available'
        } · ${mb} MB · ${formatPeak(heap.peak())}`,
      );
    } catch (e) {
      if (!controller.signal.aborted) {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setExporting(false);
      abortRef.current = null;
    }
  }, [src, meta, captions, styleId, accent, fontFamily, file, hasRange, exportRangeOnly, rangeStart, rangeEnd]);

  // Correcting a word rewrites only that caption; the preview re-renders from
  // the same array the export reads, so a fix is visible before burning in.
  const editCaption = useCallback((index: number, text: string) => {
    setCaptions((prev) =>
      prev.map((caption, i) => (i === index ? { ...caption, text } : caption)),
    );
  }, []);

  const chooseFont = useCallback(async (font: CaptionFont) => {
    setFontBusy(true);
    setError(null);
    try {
      const family = await loadCaptionFont(font);
      setFontId(font.id);
      setFontFamily(family);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setFontBusy(false);
    }
  }, []);

  const uploadFont = useCallback(async (fontFile: File) => {
    setFontBusy(true);
    setError(null);
    try {
      const family = await loadCustomFont(fontFile);
      setFontId('custom');
      setFontFamily(family);
    } catch {
      setError(`Could not read "${fontFile.name}" as a font. Try .woff2, .ttf or .otf.`);
    } finally {
      setFontBusy(false);
    }
  }, []);

  const deleteCaption = useCallback((index: number) => {
    setCaptions((prev) => removeCaption(prev, index));
  }, []);

  const nudge = useCallback((index: number, deltaMs: number) => {
    setCaptions((prev) => nudgeCaption(prev, index, deltaMs));
  }, []);

  // Whisper's timings can drift against the video as a whole; nudging every
  // caption one at a time would be unusable, so offset the lot.
  const [offsetMs, setOffsetMs] = useState(0);
  const shiftAll = useCallback((deltaMs: number) => {
    setCaptions((prev) => shiftCaptions(prev, deltaMs));
    setOffsetMs((prev) => prev + deltaMs);
  }, []);

  const inputProps = useMemo(
    () => ({
      src: src ?? '',
      captions,
      styleId,
      accent,
      fontFamily,
      // Preview only. The export overrides this to 'webcodecs', which
      // @remotion/web-renderer requires.
      videoBackend: 'html5' as const,
    }),
    [src, captions, styleId, accent, fontFamily],
  );

  const durationInFrames = meta
    ? Math.max(1, Math.floor(meta.durationInSeconds * meta.fps))
    : 1;

  const modelInfo = models.find((m) => m.name === model);

  return (
    <div className="app">
      <header className="bar">
        <div className="brand">
          <span className="mark" aria-hidden="true" />
          <div>
            <h1>Undertone</h1>
            <p>Transcription and burn-in run entirely in this browser tab.</p>
          </div>
        </div>
        <div className="badges">
          <span className={isolated ? 'pill ok' : 'pill warn'}>
            {isolated ? 'cross-origin isolated' : 'not isolated — Whisper will refuse'}
          </span>
          {/* The isolation and WebGPU badges beside this one already report what
              the browser can actually do, so this only flags an engine known to
              be unable to run the app at all. Safari does not support
              COEP: credentialless, so it can never be cross-origin isolated
              here. Firefox is a verified working target. */}
          <span className={engine === 'safari' ? 'pill warn' : 'pill'}>
            {engine === 'safari' ? 'safari — not supported' : engine}
          </span>
          {gpu ? (
            <span className={gpu === 'yes' ? 'pill ok' : 'pill'} title="Transcription is CPU-only today; this reports whether a WebGPU engine could be used instead.">
              {gpu === 'yes' ? 'webgpu available' : `webgpu ${gpu}`}
            </span>
          ) : null}
          <span className="pill" title={`Built ${__BUILD_TIME__} UTC`}>
            build {__BUILD_COMMIT__}
          </span>
        </div>
      </header>

      {error ? (
        <div className="alert" role="alert">
          <strong>
            Failed{failedStage ? ` during: ${STAGE_LABEL[failedStage]}` : ''}
          </strong>
          <span>{error}</span>
          <span className="mono diag">{describeEnvironment()}</span>
        </div>
      ) : null}

      <main className="grid">
        <section className="stage-col">
          {!src ? (
            <label
              className="drop"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (busy || exporting) return;
                const f = e.dataTransfer.files?.[0];
                if (f) onPick(f);
              }}
            >
              <input
                type="file"
                accept="video/*"
                hidden
                disabled={busy || exporting}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onPick(f);
                }}
              />
              <span className="drop-title">Drop a video here</span>
              <span className="drop-sub">or click to browse · mp4, mov, webm</span>
            </label>
          ) : (
            <div className="player-wrap">
              {meta ? (
                <ErrorBoundary label="Preview" resetKey={captions}>
                  <Player
                    ref={playerRef}
                    component={SubtitledVideo}
                    inputProps={inputProps}
                    durationInFrames={durationInFrames}
                    fps={meta.fps}
                    compositionWidth={meta.width}
                    compositionHeight={meta.height}
                    controls
                    loop
                    acknowledgeRemotionLicense
                    style={{ width: '100%', borderRadius: 10, overflow: 'hidden' }}
                  />
                </ErrorBoundary>
              ) : null}

              {meta ? (
                <Filmstrip
                  src={src}
                  durationInSeconds={meta.durationInSeconds}
                  fps={meta.fps}
                  start={rangeStart}
                  end={rangeEnd}
                  playhead={playheadSec}
                  disabled={busy}
                  onChange={(s, e) => {
                    setRangeStart(s);
                    setRangeEnd(e);
                  }}
                  onSeek={(sec) =>
                    playerRef.current?.seekTo(seekFrameFor(sec, meta.fps, durationInFrames))
                  }
                />
              ) : null}

              <div className="filebar">
                <span className="mono truncate">{file?.name}</span>
                {meta ? (
                  <span className="mono dim">
                    {meta.width}×{meta.height} ·{' '}
                    <span
                      className={meta.fpsAssumed ? 'assumed' : undefined}
                      title={
                        meta.fpsAssumed
                          ? 'This file reported no frame rate, so 30 was assumed. Captions stay in sync, but the export is encoded at 30 fps.'
                          : undefined
                      }
                    >
                      {meta.fps} fps{meta.fpsAssumed ? ' (assumed)' : ''}
                    </span>{' '}
                    · {meta.durationInSeconds.toFixed(1)}s
                  </span>
                ) : null}
                <button
                  className="ghost"
                  disabled={busy || exporting}
                  title={
                    busy || exporting
                      ? 'Finish or cancel the current job before replacing the video'
                      : undefined
                  }
                  onClick={() => {
                    setFile(null);
                    setSrc(null);
                    setMeta(null);
                    setCaptions([]);
                    setStage('idle');
                    setError(null);
                  }}
                >
                  Replace
                </button>
              </div>
            </div>
          )}

          {restored ? (
            <div className="restored">
              <span>
                Restored a transcript you made <strong>{restored}</strong> — no need to
                transcribe again unless you want to.
              </span>
              <button
                className="ghost"
                onClick={() => {
                  if (file) void deleteTranscript(fileKey(file));
                  setCaptions([]);
                  setRestored(null);
                  setStage('idle');
                }}
              >
                Discard
              </button>
            </div>
          ) : null}

          {captions.length > 0 ? (
            <Transcript
              captions={captions}
              playheadSec={playheadSec}
              onSeek={(sec) =>
                playerRef.current?.seekTo(
                  seekFrameFor(sec, meta?.fps ?? 30, durationInFrames),
                )
              }
              onEdit={editCaption}
              onDelete={deleteCaption}
              onNudge={nudge}
            />
          ) : null}
        </section>

        <aside className="panel">
          <div className="block">
            <h2>1 · Speech engine</h2>

            <div className="styles">
              {ENGINES.map((e) => (
                <button
                  key={e.id}
                  className={`style ${engineId === e.id ? 'on' : ''}`}
                  disabled={busy}
                  onClick={() => setEngineId(e.id)}
                >
                  <strong>
                    {e.name}
                    <span className={`tag-compute ${e.id === 'gpu' ? 'gpu' : 'cpu'}`}>
                      {e.id === 'gpu' ? 'GPU' : 'CPU'}
                    </span>
                  </strong>
                  <span>{e.hint}</span>
                </button>
              ))}
            </div>

            {engineId === 'gpu' ? (
              <div className="field">
                <label htmlFor="gpumodel">Model</label>
                <select
                  id="gpumodel"
                  value={gpuModel}
                  disabled={busy}
                  onChange={(e) => setGpuModel(e.target.value as WebGpuModelId)}
                >
                  {WEBGPU_MODELS.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label} — ~{m.approxMb} MB
                    </option>
                  ))}
                </select>
                <p className="help">
                  ONNX weights from the Hugging Face Hub, cached by the browser. Encoder runs
                  fp32 and the decoder q4 — fp16 encoders currently misbehave on WebGPU.
                </p>
              </div>
            ) : (
              <div className="field">
                <label htmlFor="model">Model</label>
                <select
                  id="model"
                  value={model}
                  disabled={busy}
                  onChange={(e) => setModel(e.target.value as WhisperWebModel)}
                >
                  {(models.length ? models : [{ name: model, downloadSize: 0 }]).map((m) => (
                    <option key={m.name} value={m.name}>
                      {m.name}
                      {m.downloadSize ? ` — ${formatBytes(m.downloadSize)}` : ''}
                    </option>
                  ))}
                </select>
                <p className="help">
                  Downloaded once, then cached in IndexedDB.
                  {modelInfo ? ` First run pulls ${formatBytes(modelInfo.downloadSize)}.` : ''}
                </p>
              </div>
            )}

            <div className="field">
              <label htmlFor="lang">Language</label>
              <select
                id="lang"
                value={language}
                disabled={busy}
                onChange={(e) => setLanguage(e.target.value as WhisperWebLanguage)}
              >
                {LANGUAGES.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>

            {meta && hasRange ? (
              <div className="field">
                <p className="help">
                  Transcribing <strong>{(rangeEnd - rangeStart).toFixed(1)}s</strong> of{' '}
                  {meta.durationInSeconds.toFixed(1)}s — inference cost scales with the
                  selection, not the file length.
                </p>
                <button
                  className="ghost wide"
                  disabled={busy}
                  onClick={() => {
                    setRangeStart(0);
                    setRangeEnd(meta.durationInSeconds);
                  }}
                >
                  Reset to full clip
                </button>
              </div>
            ) : null}

            <button className="primary" onClick={run} disabled={!file || busy}>
              {busy ? STAGE_LABEL[stage] : 'Generate subtitles'}
            </button>

            {busy ? (
              <div className="progress">
                <div className={`track${waiting ? ' waiting' : ''}`}>
                  {waiting ? null : <div className="fill" style={{ width: `${progress * 100}%` }} />}
                </div>
                <span className="mono dim">
                  {STAGE_LABEL[stage]} — {progressDetail}
                </span>
              </div>
            ) : null}
          </div>

          <div className="block">
            <h2>2 · Caption style</h2>
            <div className="styles">
              {CAPTION_STYLES.map((s) => (
                <button
                  key={s.id}
                  className={`style ${styleId === s.id ? 'on' : ''}`}
                  onClick={() => setStyleId(s.id)}
                >
                  <strong>{s.name}</strong>
                  <span>{s.hint}</span>
                </button>
              ))}
            </div>

            <div className="field">
              <label htmlFor="font">Caption font</label>
              <select
                id="font"
                value={fontId}
                disabled={fontBusy}
                onChange={(e) => {
                  const next = CAPTION_FONTS.find((f) => f.id === e.target.value);
                  if (next) void chooseFont(next);
                }}
              >
                {CAPTION_FONTS.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
                {fontId === 'custom' ? <option value="custom">Your font</option> : null}
              </select>

              <label className="ghost wide upload">
                <input
                  type="file"
                  accept=".woff2,.woff,.ttf,.otf,font/*"
                  hidden
                  disabled={fontBusy}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void uploadFont(f);
                  }}
                />
                {fontBusy ? 'Loading font…' : 'Use your own font file'}
              </label>
              <p className="help">
                Brand fonts stay on your machine — a font file is registered locally and never
                uploaded. Google fonts are fetched once and cached.
              </p>
            </div>

            <div className="field">
              <label>Sync</label>
              <div className="sync">
                <button className="ghost tiny" onClick={() => shiftAll(-100)}>
                  −100 ms
                </button>
                <span className="mono sync-read">
                  {offsetMs === 0 ? 'in sync' : `${offsetMs > 0 ? '+' : ''}${offsetMs} ms`}
                </span>
                <button className="ghost tiny" onClick={() => shiftAll(100)}>
                  +100 ms
                </button>
              </div>
              <p className="help">
                Moves every caption at once. Use this when the whole transcript runs early or
                late; nudge individual words in the transcript instead.
              </p>
            </div>

            <div className="field">
              <label>Highlight colour</label>
              <div className="swatches">
                {ACCENTS.map((c) => (
                  <button
                    key={c}
                    className={`swatch ${accent === c ? 'on' : ''}`}
                    style={{ background: c }}
                    aria-label={`Accent ${c}`}
                    onClick={() => setAccent(c)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="block">
            <h2>3 · Export</h2>
            {hasRange ? (
              <label className="check">
                <input
                  type="checkbox"
                  checked={exportRangeOnly}
                  disabled={exporting}
                  onChange={(e) => setExportRangeOnly(e.target.checked)}
                />
                Trim output to the same range
              </label>
            ) : null}

            <button
              className="primary"
              disabled={!captions.length || exporting || !meta}
              onClick={doExport}
            >
              {exporting ? `Rendering — ${Math.round(exportPct * 100)}%` : 'Burn in and download MP4'}
            </button>
            {exporting ? (
              <>
                <div className="progress">
                  <div className="track">
                    <div className="fill" style={{ width: `${exportPct * 100}%` }} />
                  </div>
                </div>
                <button className="ghost wide" onClick={() => abortRef.current?.abort()}>
                  Cancel
                </button>
              </>
            ) : null}
            <button
              className="ghost wide"
              disabled={!captions.length}
              onClick={() =>
                download(
                  new Blob([captionsToSrt(captions)], { type: 'text/plain' }),
                  `${(file?.name ?? 'video').replace(/\.[^.]+$/, '')}.srt`,
                )
              }
            >
              Download .srt instead
            </button>
            {exportNote ? <p className="help mono">Wrote {exportNote}</p> : null}
            <p className="help">
              Encoding uses WebCodecs with hardware acceleration where the browser offers it.
              Container and codecs are negotiated against what this browser can encode.
              Nothing is uploaded.
            </p>
          </div>
        </aside>
      </main>
    </div>
  );
};
